// App-wide toast stack (redux/slices/toastSlice). Mounted once in App.tsx — anything dispatches
// `pushToast(...)`. Non-blocking: auto-dismisses, never steals focus (NOT-FR-01-005: passive
// feedback is not an interruptive alert). Text: backend `messageCode` via i18n
// (`messages.<code>`, interpolating `params`); a missing translation falls back to `toast.generic`.
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { dismissToast, type Toast, type ToastLevel } from '@/redux/slices/toastSlice';

// Left accent stripe per level — the toast must read as distinct from SAM's chat/caption bubbles.
const LEVEL_STYLE: Record<ToastLevel, string> = {
  INFO: 'border-l-accent',
  SUCCESS: 'border-l-success',
  WARNING: 'border-l-amber-500',
};

const LEVEL_DOT: Record<ToastLevel, string> = {
  INFO: 'bg-accent',
  SUCCESS: 'bg-success',
  WARNING: 'bg-amber-500',
};

const ToastItem: React.FC<{ toast: Toast }> = ({ toast }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const timer = window.setTimeout(() => dispatch(dismissToast(toast.id)), toast.durationMs);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.durationMs, dispatch]);

  // A code with no translation must not render an empty toast — fall back to a generic string.
  const text = t(`messages.${toast.messageCode}`, { ...(toast.params ?? {}), defaultValue: t('toast.generic') });
  return (
    <button
      type="button"
      onClick={() => dispatch(dismissToast(toast.id))}
      aria-label={t('toast.dismiss')}
      className={`pointer-events-auto flex min-h-11 w-full max-w-md items-center gap-2 rounded-xl border border-ui-border border-l-4 bg-ui-raised px-4 py-2 text-sm font-medium text-ui-text shadow-xl ring-1 ring-black/5 animate-[toast-in_180ms_ease-out] ${LEVEL_STYLE[toast.level]}`}
    >
      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${LEVEL_DOT[toast.level]}`} />
      <span className="flex-1 text-left">{text}</span>
    </button>
  );
};

export const Toaster: React.FC = () => {
  const toasts = useAppSelector((s) => s.toast.items);
  // The live region stays mounted (screen readers announce changes to an EXISTING region).
  // Top of the screen, under the header: at the bottom it sat on top of SAM's realtime caption
  // and the voice bar (same spot, same moment) and was easy to miss.
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-16 z-[70] flex flex-col items-center gap-2 px-4"
    >
      {toasts.map((toast) => <ToastItem key={toast.id} toast={toast} />)}
    </div>
  );
};

export default Toaster;
